import MarkdownIt from 'markdown-it'
import MarkdownItQuestionForm, {
  question_client_plugin,
  question_client_service_plugin
} from '../lib/markdown_it_question_form'
import * as hljs from 'highlight.js'
import * as html5medialPlugin from 'markdown-it-html5-embed'
import * as emoji from 'markdown-it-emoji'
import { QuestionFieldType } from '../common/QuestionFieldType'
import { MATH_REGEX, mathLiveToLatex, checkMathLiveExist } from '../lib/mathlive'

const defaultSetting = (md: MarkdownIt.MarkdownIt) => {
  md.use(html5medialPlugin, { html5embed: { useLinkSyntax: true } })
  md.use(emoji)
}

export default function questionParser(body: string, fieldType: string = null, preview: boolean = true): string {
  const md = new MarkdownIt({
    html: true,
    linkify: true,
    typographer: true,
    breaks: true,
    langPrefix: 'language-',
    highlight: function (str, lang) {
      if (lang && hljs.getLanguage(lang)) {
        try {
          return '<pre class="hljs">' + hljs.highlight(lang, str, true).value + '</pre>'
        } catch (__) { }
      }

      return '<pre class="hljs">' + md.utils.escapeHtml(str) + '</pre>'
    }
  })

  defaultSetting(md)

  if (fieldType) {
    md.use(MarkdownItQuestionForm, { fieldType, preview })
  }

  return md.render(body)
}

export function questionClientParser(body: string, fieldType: string = null, preview: boolean = true): string {
  const md = new MarkdownIt({
    html: true,
    linkify: true,
    typographer: true,
    breaks: true,
    langPrefix: 'language-',
    highlight: function (str, lang) {
      if (lang && hljs.getLanguage(lang)) {
        try {
          return '<pre class="hljs">' + hljs.highlight(lang, str, true).value + '</pre>'
        } catch (__) { }
      }

      return '<pre class="hljs">' + md.utils.escapeHtml(str) + '</pre>'
    }
  })

  defaultSetting(md)

  if (fieldType) {
    md.use(question_client_plugin, { fieldType, preview })
  }

  return md.render(body)
}

// TextFieldの場合、TextBoxにレンダリングされますが、
// codeの場合、さっきにPreタグ変換されたので、TextBoxにレンダリング出来なかった
// https://github.com/happa/skillhub/issues/1093
// 修正仕方はTextFieldの部分をtempに変換して、レンダリングされたら、tempをTextBoxに変換する
// QuestionのTextFieldの場合、answerItemsはTextBoxです
export function questionServiceParser(
  body: string,
  questionFieldType: QuestionFieldType,
  preview: boolean = true
): string {
  const sizeMaps: number[] = []
  const scopeIds: number[] = []

  if (questionFieldType.isTextfield) {
    // TextFieldの部分をtempに変換する
    questionFieldType.answerItems.forEach(answerItem => {
      [
        `$(${answerItem.body} | ${answerItem.allotment})`,
        `$(${answerItem.body}| ${answerItem.allotment})`,
        `$(${answerItem.body} |${answerItem.allotment})`,
        `$(${answerItem.body}|${answerItem.allotment})`
      ].forEach(answerItemStr => {
        const indexOfAnswer = body.indexOf(answerItemStr)
        // Get ___ string
        if (indexOfAnswer >= 0) {
          let size = 0
          while (body[indexOfAnswer + answerItemStr.length] == '_') {
            size++
            answerItemStr = `${answerItemStr}_`
          }
          sizeMaps.push(size)
          body = body.replace(`${answerItemStr}`, 'TMP_TextField_TMP')
        }
      });
    })
  }

  if (questionFieldType.isInlineSelect) {
    questionFieldType.answerItems.forEach(answerItem => {
      if (scopeIds.indexOf(answerItem.scopeId) == -1) {
        scopeIds.push(answerItem.scopeId)
      }
    })

    scopeIds.forEach((scopeId) => {
      let inlineSelectScopeStr = questionFieldType.answerItems
        .filter((answerItem) => answerItem.scopeId == scopeId)
        .map((answerItem) => {
          return `[${answerItem.body}, ${answerItem.allotment}]`
        })
        .join(', ')

      inlineSelectScopeStr = `$(*${inlineSelectScopeStr})`

      const indexOfAnswer = body.indexOf(inlineSelectScopeStr)
      // Get ___ string
      if (indexOfAnswer >= 0) {
        let size = 0
        while (body[indexOfAnswer + inlineSelectScopeStr.length] == '_') {
          size++
          inlineSelectScopeStr = `${inlineSelectScopeStr}_`
        }
        sizeMaps.push(size)
        body = body.replace(`${inlineSelectScopeStr}`, `TMP_INLINE_SELECT_TMP_${scopeId}`)
      }
    })
  }

  // 数学の変換
  const latexs = []
  while (body.match(MATH_REGEX)) {
    const match = body.match(MATH_REGEX)
    const index = latexs.length

    latexs.push(mathLiveToLatex(match[0]))
    body = body.replace(`${match[0]}`, `TMP_MATHLIVE_TMP_${index}`)
  }

  const md = new MarkdownIt({
    html: true,
    linkify: true,
    typographer: true,
    breaks: true,
    langPrefix: 'language-',
    highlight: function (str, lang) {
      if (lang && hljs.getLanguage(lang)) {
        try {
          return '<pre class="hljs">' + hljs.highlight(lang, str, true).value + '</pre>'
        } catch (__) { }
      }

      return '<pre class="hljs">' + md.utils.escapeHtml(str) + '</pre>'
    }
  })

  defaultSetting(md)

  if (!questionFieldType.isUnknowType) {
    md.use(question_client_service_plugin, { fieldType: questionFieldType.fieldType, preview })
  }

  let renderedBody = md.render(body)

  // 数学の変換
  for (let index = 0; index < latexs.length; index++) {
    const latex = latexs[index]
    const mathHtml = (window as any).MathLive.latexToMarkup(latex)
    renderedBody = renderedBody.replace(`TMP_MATHLIVE_TMP_${index}`, mathHtml)
  }

  if (questionFieldType.isTextfield) {
    // レンダリングされたら、tempをTextBoxに変換する
    for (let index = 0; index < questionFieldType.answerItems.length; index++) {
      const answerItem = questionFieldType.answerItems[index]
      const size = sizeMaps[index]
      if (size == undefined) {
        continue
      }
      const width = Math.max(size, 10) * 20

      if (checkMathLiveExist(answerItem.body)) {
        renderedBody = renderedBody.replace(
          'TMP_TextField_TMP',
          `<span><input autocapitalize="off" autocomplete="off" autocorrect="off" spellcheck="false" type="text" class="form-control textfield-question" style="width:${width}px; max-width: 100%; display: inline" required /></span>`
        )
      } else {
        renderedBody = renderedBody.replace(
          'TMP_TextField_TMP',
          `<input autocapitalize="off" autocomplete="off" autocorrect="off" spellcheck="false" type="text" class="form-control textfield-question" style="width:${width}px; max-width: 100%; display: inline" required />`
        )
      }
    }
  }

  if (questionFieldType.isInlineSelect) {
    for (let index = 0; index < scopeIds.length; index++) {
      const size = sizeMaps[index]
      const scopeId = scopeIds[index]

      if (size == undefined) {
        continue
      }

      const options = ['<option value="" selected disabled>以下から選びなさい</option>']
      questionFieldType.answerItems
        .filter(answerItem => answerItem.scopeId == scopeId)
        .forEach(answerItem => {
          options.push(`<option value="${answerItem.body}"><span>${answerItemBodyParser(answerItem.body)}</span></option>`)
        })

      const optionHtmls = options.join(' ')
      const inlineSelectHtml = `<select class="form-control inline-select-question" style="width:${170 +
        size *
        20}px;display: inline !important;visibility: visible !important; -webkit-appearance: auto;" > ${optionHtmls} </select>`
      renderedBody = renderedBody.replace(`TMP_INLINE_SELECT_TMP_${scopeId}`, inlineSelectHtml)
    }
  }

  return renderedBody
}

export function answerItemBodyParser(anserItemBody: string) {
  let body = anserItemBody
  // 数学の変換
  const latexs = []
  while (body.match(MATH_REGEX)) {
    const match = body.match(MATH_REGEX)
    const index = latexs.length

    latexs.push(mathLiveToLatex(match[0]))
    body = body.replace(`${match[0]}`, `TMP_MATHLIVE_TMP_${index}`)
  }

  const md = new MarkdownIt({
    html: false,
    linkify: true,
    typographer: true,
    breaks: true,
    langPrefix: 'language-'
  })

  let renderedBody = md.render(body)

  // 数学の変換
  for (let index = 0; index < latexs.length; index++) {
    const latex = latexs[index]
    const mathHtml = (window as any).MathLive.latexToMarkup(latex)
    renderedBody = renderedBody.replace(`TMP_MATHLIVE_TMP_${index}`, mathHtml)
  }

  return renderedBody
}
